import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Shield, Landmark, Briefcase, CheckCircle2 } from 'lucide-react';
const Hero = () => {
  return <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-brand-navy" data-id="element-224">
      {/* Scroll Reveal Animation */}
      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1
    }} className="absolute inset-0 z-0 pointer-events-none" data-id="element-225">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-gold/10 rounded-full blur-[120px] animate-pulse" data-id="element-226" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-emerald/10 rounded-full blur-[120px] animate-pulse delay-700" data-id="element-227" />
      </motion.div>
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20" data-id="element-228">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay" data-id="element-229" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/80 to-transparent" data-id="element-230" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-12 relative z-10 grid lg:grid-cols-2 gap-12 items-center" data-id="element-231">
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-232">
          <span className="inline-block text-brand-gold font-heading font-bold tracking-[0.2em] uppercase text-[10px] mb-4" data-id="element-233">
            Three Practices. One Trusted Advisor.
          </span>
          <h1 className="text-4xl md:text-5xl font-display text-white leading-[1.1] mb-6" data-id="element-234">
            Strategic Advice for <br data-id="element-235" />
            <span className="italic" data-id="element-236">Complex</span> Financial Decisions
          </h1>
          <p className="text-base text-white/70 font-light max-w-lg mb-8 leading-relaxed" data-id="element-237">
            We help businesses grow through strategic advisory, capital
            solutions, and tax expertise. Delivering practical advice and
            disciplined execution.
          </p>

          <div className="flex flex-wrap gap-4" data-id="element-238">
            <button className="bg-brand-gold hover:bg-brand-gold/90 text-white px-6 py-3 text-xs font-bold tracking-widest uppercase transition-all flex items-center gap-3" data-id="element-239">
              Schedule a Consultation <ArrowRight size={16} data-id="element-240" />
            </button>
            <Link to="/services" className="border border-white/30 hover:border-white text-white px-6 py-3 text-xs font-bold tracking-widest uppercase transition-all" data-id="element-241">
              Explore Our Services
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-white/10 pt-8" data-id="element-242">
            {[{
            label: 'Strategic Advisory',
            value: 'Expertise'
          }, {
            label: 'Tax & Accounting',
            value: 'Precision'
          }, {
            label: 'Capital Solutions',
            value: 'Growth'
          }].map((stat, i) => <div key={i} data-id="element-243">
                <div className="text-brand-gold font-display text-2xl mb-1" data-id="element-244">
                  {stat.value}
                </div>
                <div className="text-white/50 text-xs uppercase tracking-widest" data-id="element-245">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="relative hidden lg:block" data-id="element-246">
          <div className="relative z-0 rounded-2xl overflow-hidden shadow-2xl border border-white/10" data-id="element-247">
            <img src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80" alt="Strategic Meeting" className="w-full h-[450px] object-cover" data-id="element-248" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent z-0" data-id="element-249" />
          </div>

          {/* Floating Cards */}
          <motion.div animate={{
          y: [0, -15, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-2 -right-3 bg-white p-4 rounded-xl shadow-2xl max-w-[200px] z-20" data-id="element-250">
            <div className="flex items-center gap-3 mb-2" data-id="element-251">
              <div className="p-2 bg-brand-gold/10 rounded-lg text-brand-gold" data-id="element-252">
                <TrendingUp size={20} data-id="element-253" />
              </div>
              <div className="text-brand-navy font-bold text-base" data-id="element-254">
                Growth Capital
              </div>
            </div>
            <div className="text-brand-charcoal/60 text-xs leading-relaxed" data-id="element-255">
              Tailored financial structures to fuel your expansion goals.
            </div>
          </motion.div>

          <motion.div animate={{
          y: [0, 15, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5
        }} className="absolute -bottom-6 -left-6 bg-brand-emerald p-4 rounded-xl shadow-2xl max-w-[200px] z-20" data-id="element-256">
            <div className="flex items-center gap-3 mb-2" data-id="element-257">
              <div className="p-2 bg-white/10 rounded-lg text-white" data-id="element-258">
                <Shield size={20} data-id="element-259" />
              </div>
              <div className="text-white font-bold text-base" data-id="element-260">Tax Planning</div>
            </div>
            <div className="text-white/80 text-xs leading-relaxed" data-id="element-261">
              Strategic tax optimization for corporate and private clients.
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
const ExecutiveSummary = () => {
  return <section className="py-24 bg-brand-warm-white overflow-hidden relative" data-id="element-262">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto px-6" data-id="element-263">
        <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-264">
          <div className="relative" data-id="element-265">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl" data-id="element-266">
              <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" alt="Executive Leadership" className="w-full h-full object-cover" data-id="element-267" />
            </div>
            <div className="absolute -bottom-10 -right-10 bg-brand-navy p-12 rounded-2xl shadow-2xl hidden md:block max-w-md" data-id="element-268">
              <div className="text-brand-gold mb-6" data-id="element-269">
                <svg width="40" height="30" viewBox="0 0 40 30" fill="currentColor" data-id="element-270">
                  <path d="M0 18.5C0 8.3 7.8 0 17.4 0V6.5C12.5 6.5 8.5 10.5 8.5 15.4H17.4V30H0V18.5ZM22.6 18.5C22.6 8.3 30.4 0 40 0V6.5C35.1 6.5 31.1 10.5 31.1 15.4H40V30H22.6V18.5Z" data-id="element-271" />
                </svg>
              </div>
              <p className="text-white text-xl font-display italic leading-relaxed mb-6" data-id="element-272">
                "Delivering practical advice, disciplined execution, and lasting
                partnerships."
              </p>

              <div className="h-px w-12 bg-brand-gold mb-4" data-id="element-273" />
              <div className="text-white/60 text-sm uppercase tracking-widest font-bold" data-id="element-274">
                Consilium Philosophy
              </div>
            </div>
          </div>

          <div data-id="element-275">
            <span className="text-brand-emerald font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-276">
              Our Identity
            </span>
            <h2 className="text-4xl md:text-5xl font-display text-brand-navy mb-8 leading-tight" data-id="element-277">
              A Multidisciplinary Advisory Firm Built on Trust
            </h2>
            <div className="space-y-6 text-brand-charcoal/70 text-lg leading-relaxed" data-id="element-278">
              <p data-id="element-279">
                Consilium Advisory is more than a consulting firm. We are
                strategic partners dedicated to navigating the complexities of
                modern finance and business growth for a global clientele.
              </p>
              <p data-id="element-280">
                Our approach combines deep financial expertise with strategic
                insight and real-world investment experience. We serve
                high-net-worth individuals, institutional investors, and
                entrepreneurs who demand institutional quality and personal
                attention.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-281">
              {[{
              title: 'Financial Expertise',
              desc: 'Rigorous analysis and technical mastery across tax and accounting.'
            }, {
              title: 'Strategic Insight',
              desc: 'Forward-looking guidance to navigate market shifts and opportunities.'
            }, {
              title: 'Investment Experience',
              desc: 'Practical knowledge from managing complex capital structures.'
            }, {
              title: 'Global Reach',
              desc: 'U.S. and Nigerian expertise for cross-border success.'
            }].map((item, i) => <div key={i} className="flex gap-4" data-id="element-282">
                  <div className="mt-1 text-brand-gold" data-id="element-283">
                    <CheckCircle2 size={20} data-id="element-284" />
                  </div>
                  <div data-id="element-285">
                    <h4 className="font-heading font-bold text-brand-navy mb-1" data-id="element-286">
                      {item.title}
                    </h4>
                    <p className="text-sm text-brand-charcoal/60" data-id="element-287">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
};
const Practices = () => {
  const practices = [{
    icon: <Briefcase className="w-8 h-8" data-id="element-288" />,
    title: 'Strategic Advisory',
    description: 'Comprehensive business strategy, market entry, and operational optimization for sustainable growth.',
    features: ['Market Analysis', 'Growth Strategy', 'Operational Excellence']
  }, {
    icon: <TrendingUp className="w-8 h-8" data-id="element-289" />,
    title: 'Capital Solutions',
    description: 'Navigating complex capital structures, debt advisory, and equity raising for ambitious enterprises.',
    features: ['Debt Advisory', 'Equity Raising', 'Capital Structuring']
  }, {
    icon: <Landmark className="w-8 h-8" data-id="element-290" />,
    title: 'Tax & Accounting',
    description: 'Precision-driven tax planning and accounting services for corporate and private wealth clients.',
    features: ['Tax Optimization', 'Financial Reporting', 'Compliance']
  }];
  return <section className="py-24 bg-white relative overflow-hidden" data-id="element-291">
      <div className="max-w-7xl mx-auto px-6" data-id="element-292">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-293">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-brand-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block" data-id="element-294">
            Our Core Practices
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl font-display text-brand-navy mb-6" data-id="element-295">
            Specialized Expertise for Global Ambitions
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-brand-charcoal/60 text-lg" data-id="element-296">
            We provide a unified approach to business challenges, combining
            strategic foresight with technical financial mastery.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-297">
          {practices.map((practice, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} whileHover={{
          y: -10
        }} className="bg-brand-warm-white p-10 rounded-2xl border border-brand-navy/5 hover:shadow-2xl transition-all duration-500 group" data-id="element-298">
              <div className="text-brand-gold mb-8 group-hover:scale-110 transition-transform duration-500" data-id="element-299">
                {practice.icon}
              </div>
              <h3 className="text-2xl font-display text-brand-navy mb-4" data-id="element-300">
                {practice.title}
              </h3>
              <p className="text-brand-charcoal/70 mb-8 leading-relaxed" data-id="element-301">
                {practice.description}
              </p>
              <ul className="space-y-3" data-id="element-302">
                {practice.features.map((feature, fIdx) => <li key={fIdx} className="flex items-center gap-3 text-sm font-medium text-brand-navy/80" data-id="element-303">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-gold" data-id="element-304" />
                    {feature}
                  </li>)}
              </ul>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
const Insights = () => {
  return <section className="py-24 bg-brand-navy text-white overflow-hidden" data-id="element-305">
      <div className="max-w-7xl mx-auto px-6" data-id="element-306">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8" data-id="element-307">
          <div className="max-w-2xl" data-id="element-308">
            <motion.span initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="text-brand-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block" data-id="element-309">
              Knowledge & Perspectives
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-display leading-tight" data-id="element-310">
              Strategic Insights for the <br data-id="element-311" />
              <span className="italic text-brand-gold" data-id="element-312">Modern Economy</span>
            </motion.h2>
          </div>
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-313">
            <Link to="/insights" className="text-brand-gold font-bold tracking-widest uppercase text-xs flex items-center gap-3 border-b border-brand-gold/30 pb-2 hover:border-brand-gold transition-all" data-id="element-314">
              View All Insights <ArrowRight size={16} data-id="element-315" />
            </Link>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-316">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="group cursor-pointer" data-id="element-317">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-8" data-id="element-318">
              <img src="https://cdn.magicpatterns.com/patterns/generated-images/5d380a82-65a4-4d77-9f46-183be55eaef6.jpg" alt="Global market analysis displayed in a boardroom overlooking an emerging-market city" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-319" />
              <div className="absolute top-6 left-6 bg-brand-gold text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-320">
                Strategy
              </div>
            </div>
            <h3 className="text-2xl font-display mb-4 group-hover:text-brand-gold transition-colors" data-id="element-321">
              Navigating Cross-Border Investments in Emerging Markets
            </h3>
            <p className="text-white/60 leading-relaxed mb-6" data-id="element-322">
              A comprehensive guide to identifying opportunities and managing
              risks in high-growth corridors between the US and West Africa.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold tracking-widest uppercase text-white/40" data-id="element-323">
              <span data-id="element-324">June 12, 2024</span>
              <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-325" />
              <span data-id="element-326">8 Min Read</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="group cursor-pointer" data-id="element-327">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-8" data-id="element-328">
              <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80" alt="Insight 2" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-329" />
              <div className="absolute top-6 left-6 bg-brand-emerald text-white px-4 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full" data-id="element-330">
                Tax & Finance
              </div>
            </div>
            <h3 className="text-2xl font-display mb-4 group-hover:text-brand-gold transition-colors" data-id="element-331">
              The Future of Corporate Tax Optimization in a Digital Age
            </h3>
            <p className="text-white/60 leading-relaxed mb-6" data-id="element-332">
              How modern enterprises are restructuring their financial
              operations to adapt to evolving global tax regulations and digital
              transformation.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold tracking-widest uppercase text-white/40" data-id="element-333">
              <span data-id="element-334">May 28, 2024</span>
              <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-335" />
              <span data-id="element-336">12 Min Read</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const CTA = () => {
  return <section className="py-24 bg-brand-warm-white relative overflow-hidden" data-id="element-337">
      <div className="max-w-5xl mx-auto px-6 text-center relative z-10" data-id="element-338">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-brand-navy p-16 md:p-24 rounded-[2rem] shadow-2xl relative overflow-hidden" data-id="element-339">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/10 rounded-full blur-[80px] -mr-32 -mt-32" data-id="element-340" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-emerald/10 rounded-full blur-[80px] -ml-32 -mb-32" data-id="element-341" />

          <h2 className="text-4xl md:text-5xl font-display text-white mb-8 relative z-10" data-id="element-342">
            Ready to Elevate Your <br data-id="element-343" />
            <span className="italic text-brand-gold" data-id="element-344">Strategic Position?</span>
          </h2>
          <p className="text-white/70 text-lg mb-12 max-w-2xl mx-auto relative z-10" data-id="element-345">
            Partner with Consilium Advisory for institutional-quality guidance
            tailored to your unique financial and business objectives.
          </p>
          <motion.div whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="inline-block" data-id="element-346">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold/90 text-white px-10 py-5 text-sm font-bold tracking-widest uppercase transition-all shadow-xl relative z-10 inline-block" data-id="element-347">
              Schedule a Consultation
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
export const Home = () => {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-348">
      <Hero data-id="element-349" />
      <ExecutiveSummary data-id="element-350" />
      <Practices data-id="element-351" />
      <Insights data-id="element-352" />
      <CTA data-id="element-353" />
    </motion.div>;
};